import React, { forwardRef } from 'react';
import { Placement, PopupMenu } from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';

import { LifeCycleList } from './life-cycle-list';
import { MenuGroups, SubMenu } from './types';

const itemRefsBase: Record<MenuGroups, Map<number, HTMLButtonElement>> = {
  [MenuGroups.Edit]: new Map<number, HTMLButtonElement>(),
  [MenuGroups.Lifecycle]: new Map<number, HTMLButtonElement>(),
};

enum Direction {
  Next = 'next',
  Previous = 'previous',
}

export type GoalCardMultiSelectContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside' | 'position' | 'interactionType'
> & {
  goals: Goal[];
  onClose: () => void;
  initialActiveSubMenu?: SubMenu;
};

export const GoalCardMultiSelectContextMenu = forwardRef<
  HTMLDivElement,
  GoalCardMultiSelectContextMenuProps
>(({ goals, onClose, initialActiveSubMenu, ...rest }, ref) => {
  const [openSubMenu, _setOpenSubMenu] = React.useState<SubMenu | undefined>(
    initialActiveSubMenu,
  );
  const itemRefs = React.useRef(itemRefsBase);

  const menuProps = {
    ...rest,
    position: 'bottom-start' as Placement,
    interactionType: InteractionType.Goal,
    onOutside: onClose,
  };

  const registerItem = React.useCallback(
    (group: MenuGroups, index: number, element: HTMLButtonElement | null) => {
      if (element) {
        itemRefs.current[group].set(index, element);
        return;
      }

      itemRefs.current[group].delete(index);
    },
    [],
  );

  const focusItem = React.useCallback((direction: Direction) => {
    const allRefs = [
      ...Array.from(itemRefs.current.edit.entries()),
      ...Array.from(itemRefs.current.lifecycle.entries()),
    ].map(([_, ref]) => ref);

    if (!allRefs.length) {
      return;
    }

    const currentRef = document.activeElement as HTMLButtonElement;
    const currentIndex = allRefs.indexOf(currentRef);

    // If nothing focused, focus first item
    if (currentIndex === -1) {
      allRefs[0]?.focus();
      return;
    }

    const nextPos =
      (direction === Direction.Next
        ? currentIndex + 1
        : currentIndex - 1 + allRefs.length) % allRefs.length;
    allRefs[nextPos]?.focus();
  }, []);

  useKeysDown(['ArrowDown'], (e) => {
    if (openSubMenu) {
      return;
    }
    e.preventDefault();
    focusItem(Direction.Next);
  });

  useKeysDown(['ArrowUp'], (e) => {
    if (openSubMenu) {
      return;
    }
    e.preventDefault();
    focusItem(Direction.Previous);
  });

  return (
    <>
      {!openSubMenu && (
        <PopupMenu ref={ref} {...menuProps}>
          <LifeCycleList
            goals={goals}
            onClose={onClose}
            registerItem={registerItem}
            registerGroup={MenuGroups.Lifecycle}
          />
        </PopupMenu>
      )}
    </>
  );
});
